import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ChevronRight, Info, UserX } from 'lucide-react';
import api from '../../Components/RefreshToken/RefreshToken';
import { Badge, Button, Card, EmptyState, Pagination, TableSkeleton } from '../../Components/ui';
import { formatServerDateTime } from '../../utils/serverDate';
import { mediaUrl } from '../../utils/media';
import { EMPTY, PROFILE_STATUS, displayValue, isFilled, profileCompleteness } from '../../Components/Students/studentProfile';
import { StudentsDateModal, StudentsFilterBar } from './StudentsFilters';

// "Kursga yozilmaganlar" tab'i: GET /management/students/not-enrolled (docs/api/kursga-yozilmagan-userlar-api.md).
// Javob formati /management/students bilan bir xil; sahifalash found bo'yicha, total_count — umumiy son.

export const PAGE_SIZE = 10; // "Kursga yozilganlar" bilan bir xil
const SEARCH_DELAY_MS = 400;
const NO_DATES = { from: '', to: '' };

function StudentAvatar({ user }) {
  const src = mediaUrl(user.photo);
  const [failed, setFailed] = useState(false);
  const initials = `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}` || '?';

  if (src && !failed) {
    return <img src={src} alt="" className="ui-students__avatar ui-students__avatar--img" loading="lazy" onError={() => setFailed(true)} />;
  }
  return (
    <span className="ui-students__avatar" aria-hidden="true">
      {initials}
    </span>
  );
}

function ProfileBadge({ user }) {
  const { filled, total, status } = profileCompleteness(user);
  const { label, variant } = PROFILE_STATUS[status];
  return (
    <Badge variant={variant} size="sm" title={`${filled} / ${total} maydon to'ldirilgan`}>
      {label}
    </Badge>
  );
}

const Muted = ({ children }) => <span className="ui-students__muted">{children}</span>;
const valueOrMuted = (value) => (isFilled(value) ? value : <Muted>{EMPTY}</Muted>);

function NotEnrolledStudents() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [dates, setDates] = useState(NO_DATES);
  const [draft, setDraft] = useState(NO_DATES);
  const [showFilter, setShowFilter] = useState(false);
  const [page, setPage] = useState(1);
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ loading: true, error: null, data: null });

  // Qidiruv: yozish tugagach so'rov va 1-sahifa
  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery(search.trim());
      setPage(1);
    }, SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    let active = true;
    setState((current) => ({ ...current, loading: true, error: null }));

    const params = { skip: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE };
    if (query) params.search = query;
    if (dates.from) params.from_created_at = dates.from;
    if (dates.to) params.to_created_at = dates.to;

    api
      .get('/management/students/not-enrolled', {
        params,
        headers: {
          accept: 'application/json',
          Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
        },
      })
      .then((res) => {
        if (active) setState({ loading: false, error: null, data: res?.data || null });
      })
      .catch((error) => {
        if (active) setState({ loading: false, error, data: null });
      });

    return () => {
      active = false;
    };
  }, [page, query, dates, attempt]);

  const rows = state.data?.result || [];
  const found = typeof state.data?.found === 'number' ? state.data.found : 0;
  const total = typeof state.data?.total_count === 'number' ? state.data.total_count : null;
  const totalPages = Math.ceil(found / PAGE_SIZE);

  const applyDates = (next) => {
    setDates(next);
    setDraft(next);
    setPage(1);
    setShowFilter(false);
  };

  const handlePageChange = (next) => {
    setPage(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  let body;
  if (state.loading) {
    body = <TableSkeleton rows={PAGE_SIZE} cols={5} />;
  } else if (state.error) {
    const status = state.error?.response?.status;
    body = (
      <Card padding="none">
        <EmptyState
          icon={<AlertTriangle />}
          title="Ro'yxatni yuklab bo'lmadi"
          description={
            status === 403
              ? "Bu ro'yxatni ko'rish uchun ruxsat yo'q (403)."
              : status
                ? `Server xato qaytardi (${status}). Qayta urinib ko'ring.`
                : "Internet aloqasini tekshirib, qayta urinib ko'ring."
          }
          action={
            <Button size="sm" variant="secondary" onClick={() => setAttempt((count) => count + 1)}>
              Qayta urinish
            </Button>
          }
        />
      </Card>
    );
  } else if (!rows.length) {
    body = (
      <Card padding="none">
        <EmptyState icon={<UserX />} title="Kursga yozilmagan foydalanuvchilar topilmadi" description="Qidiruv yoki filtrlarni o'zgartirib ko'ring" />
      </Card>
    );
  } else {
    body = (
      <div className="ui-table-wrap">
        <table className="ui-table ui-students__table">
          <thead>
            <tr>
              <th>Student</th>
              <th>Telefon</th>
              <th>Ro'yxatdan o'tgan</th>
              <th>Profil</th>
              <th aria-label="Amallar" />
            </tr>
          </thead>
          <tbody>
            {rows.map((user) => (
              <tr key={user.id}>
                <td>
                  <div className="ui-students__person">
                    <StudentAvatar user={user} />
                    <div className="ui-students__person-text">
                      <span className="ui-students__name">{[user.first_name, user.last_name].filter(Boolean).join(' ') || EMPTY}</span>
                      <span className="ui-students__email">{displayValue(user.email)}</span>
                    </div>
                  </div>
                </td>
                <td className="ui-students__nowrap">{valueOrMuted(user.phone_number)}</td>
                <td className="ui-students__nowrap">{formatServerDateTime(user.created_at) || <Muted>{EMPTY}</Muted>}</td>
                <td>
                  <ProfileBadge user={user} />
                </td>
                <td className="ui-students__actions">
                  <Button
                    variant="ghost"
                    size="sm"
                    rightIcon={<ChevronRight />}
                    onClick={() => navigate('/student-info', { state: { user } })}
                  >
                    Batafsil
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="ui-students__tab">
      <p className="ui-students__note">
        <Info aria-hidden="true" />
        Ro'yxatdan o'tgan, lekin birorta kursga yozilmagan foydalanuvchilar. To'lovni boshlab, tugatmaganlar ham shu yerda.
      </p>

      <StudentsFilterBar
        search={search}
        onSearchChange={(event) => setSearch(event.target.value)}
        hasDateFilter={Boolean(dates.from || dates.to)}
        onToggleFilter={() => {
          setDraft(dates);
          setShowFilter(true);
        }}
      />
      <StudentsDateModal
        open={showFilter}
        onClose={() => setShowFilter(false)}
        fromDate={draft.from}
        toDate={draft.to}
        onFromChange={(event) => setDraft((current) => ({ ...current, from: event.target.value }))}
        onToChange={(event) => setDraft((current) => ({ ...current, to: event.target.value }))}
        onClear={() => applyDates(NO_DATES)}
        onApply={() => applyDates(draft)}
      />

      {body}

      {!state.error && state.data && (
        <div className="ui-students__footer">
          <span className="ui-students__total">
            Topildi: {found} ta
            {total !== null && <span className="ui-students__muted"> · Jami kursga yozilmaganlar: {total} ta</span>}
          </span>
          {found > PAGE_SIZE && <Pagination page={page} totalPages={totalPages} onPageChange={handlePageChange} />}
        </div>
      )}
    </div>
  );
}

export default NotEnrolledStudents;
